import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Apple, Milk, ShoppingCart } from "lucide-react";
import { MainLogo } from "modules/main/assets/main_logo";

/** Fixed inner canvas dimensions (px) */
const CANVAS_W = 280;
const CANVAS_H = 180;

/** Node center positions in canvas coordinates */
const LOGO_CENTER = { x: 32, y: 90 } as const;
const CART_CENTERS = [
  { x: 256, y: 30 },
  { x: 256, y: 90 },
  { x: 256, y: 150 },
] as const;
const JUNCTION_X = 188;

/** Particle icon size (px) */
const PARTICLE_SIZE = 20;
const HALF = PARTICLE_SIZE / 2;

type CartIndex = 0 | 1 | 2;

interface ParticleDef {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  cartIndex: CartIndex;
  delay: number;
  colorClass: string;
}

/**
 * Six particles (Bottle / Apple alternating) each targeting a different cart.
 * Total cycle per particle = PARTICLE_DURATION + PARTICLE_REPEAT_DELAY = 6 s.
 * With one particle every 1 s there are always 1-2 visible, giving a smooth flow.
 */
const PARTICLES: ParticleDef[] = [
  { Icon: Milk,  cartIndex: 0, delay: 0, colorClass: "bg-orange-400" },
  { Icon: Apple,  cartIndex: 1, delay: 1, colorClass: "bg-green-500"  },
  { Icon: Milk,  cartIndex: 2, delay: 2, colorClass: "bg-orange-400" },
  { Icon: Apple,  cartIndex: 0, delay: 3, colorClass: "bg-green-500"  },
  { Icon: Milk,  cartIndex: 1, delay: 4, colorClass: "bg-orange-400" },
  { Icon: Apple,  cartIndex: 2, delay: 5, colorClass: "bg-green-500"  },
];

const PARTICLE_DURATION = 2.2;
const PARTICLE_REPEAT_DELAY = 3.8;

/**
 * Returns Framer Motion keyframe arrays for a particle traveling from
 * the logo node to the target cart following the exact L-shaped line path:
 *   Logo → horizontal → junction → vertical → cart Y → horizontal → cart
 *
 * Using 5 keypoints keeps x and y in sync so the particle never goes diagonal:
 *   - t0: appear at logo
 *   - t1: arrive at junction (end of horizontal segment), x=JX, y=logoY
 *   - t2: arrive at cart Y level (end of vertical segment), x=JX, y=cartY
 *   - t3: arrive at cart (end of branch), x=cartX, y=cartY
 *   - t4: fade out at cart
 *
 * For the middle cart there is no vertical segment so the path is just 2 hops:
 *   Logo → junction (= cart Y) → cart
 */

/** Pre-computed particle anchor points */
const SX = LOGO_CENTER.x + 22 - HALF + 4; // just right of logo node
const SY = LOGO_CENTER.y - HALF;           // logo center Y, particle-adjusted
const JX = JUNCTION_X - HALF;              // junction x, particle-adjusted
const EX = CART_CENTERS[0].x - HALF - 2;  // just left of cart node

const getParticleKeyframes = (cartIndex: CartIndex) => {
  const cart = CART_CENTERS[cartIndex];
  const ey = cart.y - HALF;

  if (cartIndex === 1) {
    // Middle cart: straight horizontal path — no vertical segment needed
    // 5 points: start | arrive junction | arrive cart | hold | fade
    return {
      x:       [SX,   JX,   EX,   EX,   EX],
      y:       [SY,   SY,   SY,   SY,   SY],
      opacity: [0,    1,    1,    1,    0],
      scale:   [0.5,  1,    1,    1,    0.4],
      times:   [0, 0.38, 0.76, 0.90, 1],
    };
  }

  // Top / bottom carts: L-shaped path following the lines
  // 5 points: start | arrive junction | turn at cart Y | arrive cart | fade
  return {
    x:       [SX,   JX,   JX,   EX,   EX],
    y:       [SY,   SY,   ey,   ey,   ey],
    opacity: [0,    1,    1,    1,    0],
    scale:   [0.5,  1,    1,    1,    0.4],
    times:   [0, 0.30, 0.56, 0.82, 1],
  };
};

/**
 * Decorative animated diagram showing how Carriapp distributes
 * shopping items from its logo to three supermarket carts.
 * Rendered in a loop; respects the user's prefers-reduced-motion setting.
 */
export const CartDistributionAnimation: React.FC = () => {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <div className="flex flex-row items-center gap-6 py-4" aria-hidden="true">
        <div className="flex items-center justify-center w-11 h-11 rounded-full bg-orange-50 border-2 border-orange-200">
          <MainLogo classname="w-7 h-7" />
        </div>
        <div className="flex flex-col gap-3">
          {CART_CENTERS.map((_, i) => (
            <div
              key={i}
              className="flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-gray-100 shadow-sm"
            >
              <ShoppingCart className="w-[18px] h-[18px] text-gray-400" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative mx-auto select-none"
      style={{ width: CANVAS_W, height: CANVAS_H }}
      aria-hidden="true"
      role="presentation"
    >
      {/* ── SVG layer: connecting lines ─────────────────────────────── */}
      <svg
        className="absolute inset-0 overflow-visible pointer-events-none"
        width={CANVAS_W}
        height={CANVAS_H}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
      >
        {/* Horizontal line: logo → junction */}
        <motion.line
          x1={LOGO_CENTER.x + 22}
          y1={LOGO_CENTER.y}
          x2={JUNCTION_X}
          y2={LOGO_CENTER.y}
          stroke="#fdba74"
          strokeWidth="1.5"
          strokeDasharray="5 5"
          strokeLinecap="round"
          initial={{ strokeDashoffset: 10 }}
          animate={{ strokeDashoffset: [10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        />

        {/* Vertical trunk: top cart ↔ bottom cart */}
        <motion.line
          x1={JUNCTION_X}
          y1={CART_CENTERS[0].y}
          x2={JUNCTION_X}
          y2={CART_CENTERS[2].y}
          stroke="#fdba74"
          strokeWidth="1.5"
          strokeDasharray="5 5"
          strokeLinecap="round"
          initial={{ strokeDashoffset: 10 }}
          animate={{ strokeDashoffset: [10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        />

        {/* Horizontal branches: junction → each cart */}
        {CART_CENTERS.map((cart, i) => (
          <motion.line
            key={`branch-${i}`}
            x1={JUNCTION_X}
            y1={cart.y}
            x2={cart.x - 18}
            y2={cart.y}
            stroke="#fdba74"
            strokeWidth="1.5"
            strokeDasharray="5 5"
            strokeLinecap="round"
            initial={{ strokeDashoffset: 10 }}
            animate={{ strokeDashoffset: [10, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "linear",
              delay: i * 0.25,
            }}
          />
        ))}

        {/* Junction dot */}
        <circle
          cx={JUNCTION_X}
          cy={LOGO_CENTER.y}
          r={3}
          fill="#fb923c"
          opacity={0.6}
        />
      </svg>

      {/* ── Logo node ────────────────────────────────────────────────── */}
      <motion.div
        className="absolute flex items-center justify-center rounded-full bg-orange-50 border-2 border-orange-200 shadow-lg shadow-orange-100"
        style={{
          width: 44,
          height: 44,
          left: LOGO_CENTER.x - 22,
          top: LOGO_CENTER.y - 22,
        }}
        animate={{
          scale: [1, 1.07, 1],
          boxShadow: [
            "0 0 0 0px rgba(249,115,22,0.15)",
            "0 0 0 7px rgba(249,115,22,0.04)",
            "0 0 0 0px rgba(249,115,22,0.15)",
          ],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <MainLogo classname="w-7 h-7" />
      </motion.div>

      {/* ── Cart nodes ───────────────────────────────────────────────── */}
      {CART_CENTERS.map((cart, i) => (
        <motion.div
          key={`cart-${i}`}
          className="absolute flex items-center justify-center rounded-xl bg-white border border-gray-100 shadow-sm"
          style={{
            width: 36,
            height: 36,
            left: cart.x - 18,
            top: cart.y - 18,
          }}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{
            duration: 2.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.7,
          }}
        >
          <ShoppingCart className="w-[18px] h-[18px] text-gray-400" />
        </motion.div>
      ))}

      {/* ── Animated particles ───────────────────────────────────────── */}
      {PARTICLES.map((particle, i) => {
        const kf = getParticleKeyframes(particle.cartIndex);
        return (
          <motion.div
            key={`particle-${i}`}
            className={`absolute flex items-center justify-center rounded-full shadow-sm ${particle.colorClass}`}
            style={{
              width: PARTICLE_SIZE,
              height: PARTICLE_SIZE,
              left: 0,
              top: 0,
            }}
            initial={{ x: kf.x[0], y: kf.y[0], opacity: 0 }}
            animate={{
              x:       kf.x,
              y:       kf.y,
              opacity: kf.opacity,
              scale:   kf.scale,
            }}
            transition={{
              duration:      PARTICLE_DURATION,
              times:         kf.times,
              repeat:        Infinity,
              repeatDelay:   PARTICLE_REPEAT_DELAY,
              delay:         particle.delay,
              ease:          "easeInOut",
            }}
          >
            <particle.Icon className="w-[11px] h-[11px] text-white" />
          </motion.div>
        );
      })}
    </div>
  );
};

export default CartDistributionAnimation;
